import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { useSeries } from '@jwp/ott-hooks-react/src/series/useSeries';
import { useEpisodes } from '@jwp/ott-hooks-react/src/series/useEpisodes';
import { useSeriesLookup } from '@jwp/ott-hooks-react/src/series/useSeriesLookup';
import { getFiltersFromSeries } from '@jwp/ott-common/src/utils/series';
import { createVideoFormatMetadata, createVideoMetadata } from '@jwp/ott-common/src/utils/metadata';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';

import { useSeriesEpisode } from './useSeriesEpisode';

export default function useSeriesData(data: PlaylistItem, seriesId: string, episodeIdParam: string | null) {
  const { t } = useTranslation('common');

  // Config
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { features, custom } = config;
  const isFavoritesEnabled: boolean = Boolean(features?.favoritesList);
  const isSharingEnabled: boolean = Boolean(!custom?.disableShare);

  // Series
  const { isLoading: isSeriesDataLoading, data: series } = useSeries(seriesId);
  const { isLoading: isRecommendationsLoading, data: recommendations } = usePlaylist(features?.recommendationsPlaylist || '', {
    related_media_id: seriesId,
    media_filtering: `contentType:${data.contentType}`, // nice to have: object notation for filters
  });

  // Watch history
  const watchHistoryDictionary = useWatchHistoryStore((state) => state.getDictionaryWithEpisodes());

  // Episode (Deeplinked || watch progress || first episode)
  const {
    allEpisodes,
    isFetching: isEpisodesFetching,
    isLoading: isEpisodesLoading,
    fetchNextPage: fetchNextEpisodes,
    hasNextPage: hasNextEpisodesPage,
  } = useEpisodes(seriesId, undefined, { enabled: true, usePlaceholderData: true });
  const { episode, nextEpisode, firstEpisode, showEpisodeInfo, resolveEpisode } = useSeriesEpisode(seriesId, allEpisodes, episodeIdParam || undefined);
  const { isLoading: isSeriesDictionaryLoading, data: episodeInSeries } = useSeriesLookup(episode?.mediaid || undefined);

  // UI
  const filters = useMemo(() => getFiltersFromSeries(series), [series]);
  const [activeSeason, setActiveSeason] = useState<string | undefined>(undefined);
  const activeSeasonParam = activeSeason || episode?.seasonNumber || '1';
  const hasRecommendations = Boolean(recommendations?.playlist?.length);

  // User, entitlement
  const { isEntitled } = useEntitlement(episode);
  const episodesEntitlements = useEntitlement(allEpisodes);
  const areEpisodesEntitled = episodesEntitlements.reduce((acc, { mediaid, isEntitled }) => {
    return { ...acc, [mediaid!]: isEntitled };
  }, {} as { [mediaid: string]: boolean });
  const { isLoggedIn, hasSubscription } = useAccountStore(({ user, subscription }) => ({ isLoggedIn: !!user, hasSubscription: !!subscription }), shallow);

  const episodeMetadata = useMemo(
    () =>
      episodeInSeries && {
        episodeNumber: typeof episodeInSeries.episode_number === 'number' ? String(episodeInSeries.episode_number) : episodeInSeries.episode_number || '',
        seasonNumber: typeof episodeInSeries.season_number === 'number' ? String(episodeInSeries.season_number) : episodeInSeries.season_number || '',
      },
    [episodeInSeries],
  );

  const episodesPlaylist = useMemo(() => {
    const seasonEpisodes = allEpisodes.filter((episode) => String(episode.seasonNumber) === String(activeSeasonParam));

    return {
      title: data.title,
      description: data.description,
      feedid: series?.series_id,
      playlist: seasonEpisodes || [],
    };
  }, [data, series, allEpisodes, activeSeasonParam]);

  const season = useMemo(() => {
    return {
      value: activeSeasonParam,
      year: firstEpisode?.year ? (firstEpisode.year as number) : firstEpisode?.pubdate ? new Date(firstEpisode.pubdate * 1000).getFullYear() : undefined,
      description: series?.seasons?.find((s) => String(s.season_number) === activeSeasonParam)?.season_description,
    };
  }, [activeSeasonParam, series, firstEpisode]);

  const seriesMetadata =
    (series?.season_count ?? 0) > 1
      ? { seasonsLabel: t('video:total_seasons', { count: series?.season_count }) }
      : { episodesLabel: t('video:total_episodes', { count: series?.episode_count }) };

  const primaryMetadata = [
    ...createVideoMetadata(data, {
      ...seriesMetadata,
      hoursAbbreviation: t('common:abbreviation.hours'),
      minutesAbbreviation: t('common:abbreviation.minutes'),
    }),
    t('video:series'),
  ];
  const videoFormats = createVideoFormatMetadata(data);
  const videoRating = [data.rating, data.ratingComponents].filter(Boolean);

  const getCardSeriesUrl = (toSeries: PlaylistItem) => mediaURL({ id: toSeries.mediaid, title: toSeries.title });

  const isLoading = isSeriesDataLoading || isSeriesDictionaryLoading || isEpisodesLoading;

  return {
    series,
    episode,
    initialEpisodeId: !!episodeIdParam,
    showEpisodeInfo,
    isFirstEpisode: !!firstEpisode && episode?.mediaid === firstEpisode.mediaid,
    season,
    episodeMetadata,
    filters,
    setActiveSeason,
    episodesPlaylist,
    recommendations,
    accessModel,
    nextItem: nextEpisode,
    primaryMetadata,
    seriesMetadata,
    isRecommendationsLoading,
    isLoading: isLoading,
    isEpisodesLoading,
    isEpisodesFetching,
    isEntitled,
    areEpisodesEntitled,
    isFavoritesEnabled,
    isSharingEnabled,
    isLoggedIn,
    hasSubscription,
    hasRecommendations,
    hasNextEpisodesPage,
    watchHistory: watchHistoryDictionary,
    videoFormats,
    videoRating,
    fetchNextEpisodes,
    getCardSeriesUrl,
    resolveEpisode,
  };
}
